/**
 * The atlas's own drawing geometry (Story #5648 build v2, figure S4).
 *
 * Pure, and separate from the component, so each shape the map claims can be
 * tested on its own: the corridor a bridge really is, the outward direction a
 * frontier arrow points, and the nudge that keeps two invention names off each
 * other. All three came out of round 1 of the whole-screen review — a bridge
 * drawn with the hole's ring, a frontier arrow buried in the middle of the map
 * so a counted 7 showed as 6, and names painted through each other.
 */
import type { PortfolioMapLayout } from '../../types/whitespace'

/** Same viewport the atlas draws into. */
export const VIEWPORT = { width: 900, height: 560, padding: 44 }

export type Point = [number, number]

export interface ShapeMarker {
  id: string
  kind: 'hole' | 'bridge' | 'frontier'
  /** The run's own centroid, in layout space. */
  at: Point
  /** The run's own boundary record ids — keys into the portfolio_map layout. */
  boundary: string[]
}

export interface DrawnMarker extends Omit<ShapeMarker, 'at'> {
  /** The centroid, projected into the canvas. */
  at: Point
  /** Frontier only: where the ray starts, where the head lands, and where its
   * label sits. The tail is on the outer edge of whatever shape the marker sits
   * in, so the drawn line never runs across a footprint or through a star. */
  tail?: Point
  head?: Point
  label?: Point
  /** Bridge only: the dashed corridor path, and the two ends it runs between —
   * kept so the pill can be placed ON the curve rather than near it. */
  curve?: string
  ends?: [Point, Point]
}

/**
 * How far a name may sit from its own mark before it needs a leader line
 * (review round 4, item 3). Beyond this a reader cannot tell which name belongs
 * to which shape — measured on the round-4 capture, "Solid Oxide Fuel Cell Stack
 * (Duplicate Filing)" sat 183px from its own star while a DIFFERENT invention's
 * name sat 56px from it. The placer prefers a slot inside the cap; anything that
 * still ends up outside gets a leader drawn back to its own star, so the pairing
 * is never left to proximity.
 */
export const NAME_LEADER_CAP = 40

/**
 * The clear space a frontier label (F1…F7) demands around every invention name
 * and pill. Round 5 measured "F5" 3px from "Cold-Chain Vaccine" on the same
 * baseline at the same weight — the same 3px that separates the two words
 * INSIDE the name — so the map read "Cold-Chain Vaccine F5" as one phrase. A
 * gap the size of a word space is not clearance.
 */
export const F_LABEL_CLEARANCE = 8

/**
 * The clear space a LEADER keeps from every document dot it is not landing on.
 * Round 7 measured the Solid Oxide leader passing 0.6px from a dot's centre —
 * the dot sat on the line — where round 6's leader had cleared every mark by
 * 10.2px. That 10px is the number this restores and holds.
 */
export const LEADER_DOT_CLEARANCE = 10

/**
 * The clear space a leader keeps from every piece of INK it is not landing on —
 * a frontier label, an arrowhead, a name, a pill. Round 8 drew a leader's
 * horizontal run through the F6 label: label ink 0.50px from the centre-line,
 * the dash run visibly broken where the glyphs paint over it, and the nearest
 * arrowhead 4.50px. Same class as round 7's dot, moved from the shaft to the
 * label — so it is measured the same way, as a number with a floor.
 */
export const LEADER_INK_CLEARANCE = 10

/**
 * What the joint solve aims a leader at. Clearance beyond this buys nothing, so
 * the placer stops trading position for it and keeps the name near its mark.
 * 7.6 is the reviewer's own measured target for the one name on this map that
 * needs a leader at all.
 */
export const LEADER_TARGET_CLEARANCE = 7.6

/** A crossing has to read as a crossing: two lines meeting at a shallow angle
 * read as one line with a kink. */
export const BRIDGE_CROSS_MIN_ANGLE = 30

/** And it has to happen in the open, not where the bridge ends or where the
 * amber ground and its chip are, or it reads as touching them. */
export const BRIDGE_CROSS_MIN_CLEAR = 12

/** A leader may not sprawl. Round 11's route bounded a 303.7x90px rectangle —
 * 5.67% of the canvas — and ran 1.43x the straight distance, which is not a
 * line pointing at something, it is an enclosure with an arrow on the end. */
export const LEADER_MAX_AREA_FRACTION = 0.02
export const LEADER_MAX_LENGTH_FACTOR = 1.2

/** The two ends of a bridge, taken from its OWN boundary records: the pair of
 * plotted documents furthest apart along the corridor. Returns nothing when
 * fewer than two of those records are on this map — a corridor needs two ends,
 * and inventing one would be drawing a shape the run never found. */
export function bridgeEnds(
  boundary: string[],
  layout: PortfolioMapLayout,
  project: (p: Point) => Point,
): [Point, Point] | null {
  const pts = boundary
    .map((id) => layout[id])
    .filter((p): p is Point => Array.isArray(p) && p.length === 2)
    .map((p) => project(p))
  if (pts.length < 2) return null
  let best: [Point, Point] = [pts[0], pts[1]]
  let bestD = -1
  for (let i = 0; i < pts.length; i += 1) {
    for (let j = i + 1; j < pts.length; j += 1) {
      const d = (pts[i][0] - pts[j][0]) ** 2 + (pts[i][1] - pts[j][1]) ** 2
      if (d > bestD) {
        bestD = d
        best = [pts[i], pts[j]]
      }
    }
  }
  return bestD > 0 ? best : null
}

/** A quadratic through the bridge's real centroid: the control point is placed
 * so the curve passes exactly through it at its midpoint, which is what makes
 * the drawn corridor agree with the number in the tile. */
export function bridgeCurve(a: Point, b: Point, through: Point): string {
  const [cx, cy] = bridgeControl(a, b, through)
  return `M${a[0].toFixed(1)},${a[1].toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${b[0].toFixed(1)},${b[1].toFixed(1)}`
}

/** The quadratic's control point — the same one bridgeCurve draws with, so a
 * caller can ask where the drawn curve actually IS. */
export function bridgeControl(a: Point, b: Point, through: Point): Point {
  return [2 * through[0] - (a[0] + b[0]) / 2, 2 * through[1] - (a[1] + b[1]) / 2]
}

/** The quadratic as the polyline it is painted as, so a line-crossing test can
 * treat the drawn bridge as the drawn lines it really is. */
export function polylineOfQuadratic(a: Point, c: Point, b: Point, steps = 12): Segment[] {
  const out: Segment[] = []
  let prev = pointOnQuadratic(a, c, b, 0)
  for (let i = 1; i <= steps; i += 1) {
    const next = pointOnQuadratic(a, c, b, i / steps)
    out.push({ a: prev, b: next })
    prev = next
  }
  return out
}

/** A point on that quadratic at parameter t. */
export function pointOnQuadratic(a: Point, c: Point, b: Point, t: number): Point {
  const u = 1 - t
  return [
    u * u * a[0] + 2 * u * t * c[0] + t * t * b[0],
    u * u * a[1] + 2 * u * t * c[1] + t * t * b[1],
  ]
}

/**
 * WHERE A BRIDGE'S PILL GOES (review round 4, item 4). The B1 pill had drifted
 * 13-19px clear of the curve it names, because it was placed by the same free
 * 2-D solver the names use — which is free to move it anywhere, including off
 * the thing it labels. A pill that names a curve has to touch that curve, so
 * this solver only ever moves it ALONG the curve: it scores the pill's box at
 * sampled points and returns the best, which is on the curve by construction.
 *
 * The middle is preferred (the figure's pill sits mid-corridor), so the score
 * carries a small pull toward t=0.5 that only decides between equally clear
 * slots.
 */
export function placePillOnCurve(
  a: Point,
  c: Point,
  b: Point,
  size: { width: number; height: number },
  obstacles: LabelObstacles = {},
  viewport: { width: number; height: number } = VIEWPORT,
): { x: number; y: number; t: number } {
  const footprints = obstacles.footprints ?? []
  const discs = obstacles.discs ?? []
  const boxes = obstacles.boxes ?? []
  let best = { x: 0, y: 0, t: 0.5 }
  let bestCost = Number.POSITIVE_INFINITY
  for (let t = 0.15; t <= 0.851; t += 0.05) {
    const [px, py] = pointOnQuadratic(a, c, b, t)
    // The rect is drawn from (y - 13) to (y + 5), so its centre is 4px above
    // the baseline: anchoring at py + 4 puts the pill's middle ON the curve.
    const x = px
    const y = py + 4
    const box = labelBoxAt({ key: 'pill', x, y, width: size.width, height: size.height })
    if (box.left < 2 || box.right > viewport.width - 2) continue
    if (box.top < 2 || box.bottom > viewport.height - 2) continue
    let cost = Math.abs(t - 0.5) / 100
    for (const bx of boxes) if (boxesOverlap(box, bx)) cost += 9
    for (const e of footprints) if (boxHitsEllipse(box, e, 2)) cost += 6
    for (const d of discs) if (boxHitsDisc(box, d, 2)) cost += 8
    if (cost < bestCost) {
      bestCost = cost
      best = { x, y, t }
    }
  }
  if (bestCost === Number.POSITIVE_INFINITY) {
    const [px, py] = pointOnQuadratic(a, c, b, 0.5)
    return { x: px, y: py + 4, t: 0.5 }
  }
  return best
}

/** An obstacle the map has already drawn: a footprint ellipse, or a small disc
 * such as a centre star or a document dot. */
export interface Ellipse { cx: number; cy: number; rx: number; ry: number; key?: string }
export interface Disc { cx: number; cy: number; r: number; key?: string }

/** Is the point inside this ellipse, allowing for a margin of clear space? */
export function insideEllipse(p: Point, e: Ellipse, margin = 0): boolean {
  const rx = e.rx + margin
  const ry = e.ry + margin
  if (rx <= 0 || ry <= 0) return false
  const nx = (p[0] - e.cx) / rx
  const ny = (p[1] - e.cy) / ry
  return nx * nx + ny * ny <= 1
}

export function insideDisc(p: Point, d: Disc, margin = 0): boolean {
  return Math.hypot(p[0] - d.cx, p[1] - d.cy) <= d.r + margin
}

/**
 * Does this drawn line touch this ellipse? Scaling by (1/rx, 1/ry) turns the
 * ellipse into a unit circle and the segment into another segment, so the
 * question becomes "is the closest point of the scaled segment within 1 of the
 * origin" — exact, with no sampling to fall between.
 *
 * The arrow-vs-shape assertion needs this: sampling the line at intervals can
 * step straight over a star (review round 4, item 2 — F2's line ran the full
 * height of a footprint and broke a star's arm).
 */
export function segmentHitsEllipse(s: Segment, e: Ellipse, margin = 0): boolean {
  const rx = e.rx + margin
  const ry = e.ry + margin
  if (rx <= 0 || ry <= 0) return false
  const ax = (s.a[0] - e.cx) / rx
  const ay = (s.a[1] - e.cy) / ry
  const bx = (s.b[0] - e.cx) / rx
  const by = (s.b[1] - e.cy) / ry
  const dx = bx - ax
  const dy = by - ay
  const len2 = dx * dx + dy * dy
  const t = len2 < 1e-12 ? 0 : Math.min(1, Math.max(0, -(ax * dx + ay * dy) / len2))
  const px = ax + dx * t
  const py = ay + dy * t
  return px * px + py * py <= 1
}

/** Same question for a disc — a centre star or a document dot. */
export function segmentHitsDisc(s: Segment, d: Disc, margin = 0): boolean {
  return segmentHitsEllipse(s, { cx: d.cx, cy: d.cy, rx: d.r, ry: d.r }, margin)
}

export interface FrontierObstacles {
  footprints?: Ellipse[]
  discs?: Disc[]
  /** Half the arrowhead's own size, so the head clears a shape by its whole
   * triangle rather than by its tip. */
  headRadius?: number
}

/**
 * Where a frontier arrow points: away from the middle of the map, so an edge
 * direction reads as one.
 *
 * ROUND 2 FIXED THE ROTATION BUG. The head used to be clamped into the viewport
 * one AXIS AT A TIME, which can move it to the wrong side of the marker — and
 * because the SVG arrowhead takes its angle from the line it terminates, a head
 * that ends up behind the marker draws an arrow pointing back into the map. Three
 * of seven arrows were wrong that way (one a full 180°). The reach is now solved
 * along the outward RAY as a single scalar, so the head can only ever be further
 * out than the marker, and the drawn angle is the outward angle by construction.
 *
 * ROUND 2 ALSO CLEARS THE SHAPES. A head that lands inside an invention's
 * footprint, or on its centre star, reads as pointing INTO that invention. The
 * reach is pushed out past any footprint or star it would otherwise land on,
 * within what the canvas allows.
 *
 * ROUND 4 CLEARS THE LINE AS WELL AS THE HEAD. A frontier run's centroid can
 * sit INSIDE an invention's footprint, and the ray was drawn from there — so
 * F2's line ran the full height of the Marine footprint and broke its centre
 * star, while its head was perfectly clear. In the figure every ray starts at a
 * footprint's outer edge and points outward into empty canvas. So the tail is
 * walked outward to the last point on the ray that still touches a footprint or
 * a star, and the line starts just past it: on the shape's edge when the marker
 * is inside one, and at the marker itself when it is not.
 */
export function frontierArrow(
  at: Point,
  centre: Point,
  index: number,
  viewport: { width: number; height: number } = VIEWPORT,
  obstacles: FrontierObstacles = {},
): { tail: Point; head: Point; label: Point; dir: Point } {
  let dx = at[0] - centre[0]
  let dy = at[1] - centre[1]
  const len = Math.hypot(dx, dy)
  if (len < 1) {
    // A marker sitting on the middle has no outward direction of its own, so
    // these fan out by index rather than piling on each other.
    const angle = (index * 2 * Math.PI) / 7
    dx = Math.cos(angle)
    dy = Math.sin(angle)
  } else {
    dx /= len
    dy /= len
  }

  const PAD = 16
  const LABEL_GAP = 15
  // Solved as one scalar on the ray — never per axis — so the head stays
  // outward of `at` whatever the geometry.
  const limitFor = (d: number, from: number, lo: number, hi: number): number => {
    if (Math.abs(d) < 1e-6) return Number.POSITIVE_INFINITY
    return d > 0 ? (hi - from) / d : (lo - from) / d
  }
  // How far the HEAD can run before it leaves the padded canvas, and how far
  // before the LABEL would. They are different limits, and conflating them cost
  // the head 15px of reach it was entitled to — which is part of why F2 came out
  // a stub. The label's preferred spot uses the tighter one; when the head has
  // to go further to earn a real shaft, the label placer clamps it back on.
  const headMaxT = Math.max(
    0,
    Math.min(
      limitFor(dx, at[0], PAD, viewport.width - PAD),
      limitFor(dy, at[1], PAD, viewport.height - PAD),
    ),
  )
  const maxT = Math.max(0, headMaxT - LABEL_GAP)

  const MIN_REACH = 26
  const WANT_REACH = 74
  const pointAt = (t: number): Point => [at[0] + dx * t, at[1] + dy * t]
  // ROUND 3: clear by the arrowhead's FULL EXTENT, not by its apex. The marker
  // triangle is ~7px on a side, so an apex sitting a pixel outside a footprint
  // still draws the head across that footprint's stroke — which is exactly what
  // F2 was doing (apex clear, base corner at radius 43.6 of a 43px circle).
  const HEAD_RADIUS = obstacles.headRadius ?? 9
  const isClear = (p: Point): boolean =>
    !(obstacles.footprints ?? []).some((e) =>
      insideEllipse(p, e, HEAD_RADIUS + 4) ||
      boxHitsEllipse(
        { left: p[0] - HEAD_RADIUS, right: p[0] + HEAD_RADIUS, top: p[1] - HEAD_RADIUS, bottom: p[1] + HEAD_RADIUS },
        e,
        4,
      ),
    ) &&
    !(obstacles.discs ?? []).some((d) => insideDisc(p, d, HEAD_RADIUS + 4))

  // WHERE THE LINE STARTS — solved BEFORE the head, because it decides how far
  // out the head has to be. Walk the whole allowed ray and remember the last
  // step still touching a footprint or a centre star; past that point the ray is
  // in clear canvas for good, since nothing further out was inside anything.
  // A marker sitting in clear canvas never enters the loop and keeps t = 0,
  // which is the marker itself — exactly the two cases the figure shows.
  const stars = (obstacles.discs ?? []).filter((d) => d.key !== undefined)
  const onShape = (p: Point): boolean =>
    (obstacles.footprints ?? []).some((e) => insideEllipse(p, e, 2)) ||
    stars.some((d) => insideDisc(p, d, 3))
  let lastInside = -1
  for (let step = 0; step <= headMaxT; step += 0.5) {
    if (onShape(pointAt(step))) lastInside = step
  }
  // ROUND 5, ITEM 2: TRIMMING THE TAIL MUST EXTEND THE RAY, NOT SHORTEN IT.
  // F2 came out 19px long with 11px of that arrowhead — two dashes, reading as
  // a floating arrowhead rather than a direction, while its siblings ran 51-77.
  // Cutting the tail back to a footprint's edge takes length off the SHAFT, so
  // the head has to move outward by the same amount to keep one.
  const MIN_SHAFT = 30
  const WANT_SHAFT = 58
  const tailT = lastInside < 0 ? 0 : Math.min(lastInside + 3, headMaxT)
  // MIN_REACH is a floor, never a ceiling: a marker that already sits outside
  // the padded canvas has headMaxT = 0, and it still gets a ray long enough to
  // carry an arrowhead and point outward — clamping to headMaxT there would
  // collapse it to zero length and leave the head with no direction at all.
  const floor = Math.max(MIN_REACH, Math.min(tailT + MIN_SHAFT, headMaxT))
  // The head may use the head's own limit — further out than the label's — when
  // that is what a real shaft costs.
  const reachMax = Math.max(maxT, floor)
  // AIM FOR A SHAFT LIKE ITS SIBLINGS', not for the floor. The minimum is what
  // a ray is entitled to; a ray whose tail was trimmed deep inside a shape
  // should still read like the others (51-77px on this map, ~80 in the figure)
  // whenever the canvas has the room, rather than sitting at the legal minimum.
  const want = Math.min(Math.max(WANT_REACH, tailT + WANT_SHAFT), reachMax)

  // Walk outward from the wanted reach; take the first clear spot, else the
  // furthest the canvas allows.
  let t = Math.max(floor, Math.min(want, Math.max(MIN_REACH, reachMax)))
  if (reachMax >= MIN_REACH) {
    let found = false
    const from = Math.max(floor, want)
    for (let candidate = from; candidate <= reachMax + 0.01; candidate += 8) {
      if (isClear(pointAt(candidate))) {
        t = candidate
        found = true
        break
      }
    }
    if (!found) {
      // Try shorter, but never inside the floor the tail imposes.
      for (let candidate = from; candidate >= floor; candidate -= 8) {
        if (isClear(pointAt(candidate))) {
          t = candidate
          found = true
          break
        }
      }
    }
    if (!found) t = Math.max(floor, Math.min(want, reachMax))
  }

  const head = pointAt(t)
  const label = pointAt(t + LABEL_GAP)
  return { tail: pointAt(Math.min(tailT, t)), head, label, dir: [dx, dy] }
}

/** The angle a line from `from` to `to` is drawn at, in degrees. */
export function angleOf(from: Point, to: Point): number {
  return (Math.atan2(to[1] - from[1], to[0] - from[0]) * 180) / Math.PI
}

/** Smallest difference between two angles, in degrees (0-180). */
export function angleGap(a: number, b: number): number {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

/** A drawn line the map already has — an arrow, or a chord of the bridge curve. */
export interface Segment { a: Point; b: Point }

/** A label's own footprint on the canvas, centred on (x, y). */
export interface LabelBox { key: string; x: number; y: number; width: number; height: number }

/** A rectangle already drawn — a B/H pill, or a frontier label. */
export interface Box { key?: string; left: number; top: number; right: number; bottom: number }

/** ROUND 13/14 — the roster's colour chip, echoed on the canvas beside a name
 * the map could not seat next to its own mark. The roster row draws a 12px
 * chip; at 7px it reads with the same weight beside 11.5px text without
 * becoming a mark in its own right. GAP keeps it off the text so it reads as a
 * chip and not as a letter; LINE_H is the line height wrappedLabelSize measures
 * with, and the chip is centred on the first line by the same 0.3-of-a-line
 * rule labelBoxAt uses to seat a box on its baseline. */
export const NAME_SWATCH = 7
export const NAME_SWATCH_GAP = 3
export const NAME_LINE_H = 12

/**
 * WHERE A STRAY NAME'S COLOUR CHIP GOES — before its first line if that slot is
 * clear, after it if not, and NOWHERE if neither is.
 *
 * Both slots are judged by the same predicate; preferring the first is the only
 * asymmetry, because that is where a chip reads as belonging to the text that
 * follows it. Returning null is a real answer, not a failure: a chip clipped by
 * the frame, or drawn over somebody else's mark, is worse than the unpaired
 * name it was meant to rescue — the same judgement round 12 made about a false
 * leader. Callers must gate the "carries its colour" wording on what this
 * actually returns, never on the stray set.
 *
 * It measures against the FIRST LINE, not the name's box. A wrapped name's box
 * is as wide as its WIDEST line, so on a narrower line the box edge is empty
 * space and a chip parked there floats away from the text it belongs to. On the
 * S4 fixture that is not cosmetic: anchored on the box the chip lands on a
 * neighbouring footprint's outline; anchored on the first line it clears every
 * footprint by the placer's own 2px margin.
 *
 * `clearOfInk` is the caller's obstacle test. The frame is checked here because
 * it is intrinsic — a chip outside it is not drawn, it is clipped.
 */
export function placeNameChip(
  name: { x: number; y: number; lines: string[] },
  clearOfInk: (box: Box) => boolean,
  viewport: { width: number; height: number } = VIEWPORT,
  inset: number = F_LABEL_CLEARANCE,
  measure: MeasureText = labelWidth,
  fontSize = 11.5,
): { x: number; y: number } | null {
  const firstBaseline = name.lines.length > 1 ? name.y - NAME_LINE_H : name.y
  const top = firstBaseline - NAME_LINE_H * 0.3 - NAME_SWATCH / 2
  const half = measure(name.lines[0] ?? '', fontSize) / 2
  for (const left of [
    name.x - half - NAME_SWATCH_GAP - NAME_SWATCH,
    name.x + half + NAME_SWATCH_GAP,
  ]) {
    const box: Box = { left, right: left + NAME_SWATCH, top, bottom: top + NAME_SWATCH }
    if (box.left < inset || box.right > viewport.width - inset) continue
    if (box.top < inset || box.bottom > viewport.height - inset) continue
    if (!clearOfInk(box)) continue
    return { x: left, y: top }
  }
  return null
}

/** Roughly how wide this text renders at this size. Inter at 11.5px averages a
 * little over half the font size per character; slightly generous on purpose,
 * because a label that clears by a pixel still looks like a collision. */
export function labelWidth(text: string, fontSize = 11.5): number {
  return Math.max(24, text.length * fontSize * 0.56)
}

/**
 * HOW WIDE A PIECE OF TEXT RENDERS — injected, so one placer can serve two maps
 * that draw at different sizes and weights.
 *
 * The atlas's own default is `labelWidth`'s estimate for its 11.5px/600 face.
 * The S5 entity map draws at 10.5px/700 and MEASURES its labels with canvas,
 * where the real advance is 7.29-7.80px per character against this estimate's
 * 6.44 — a 15-20% gap, which is exactly the size of gap that clipped four
 * labels in an earlier round. So the caller passes its own measurement in
 * rather than the geometry guessing on its behalf.
 */
export type MeasureText = (text: string, fontSize: number) => number

/** The box a label occupies. `y` is the text BASELINE, so the box sits mostly
 * above it. Exported so a test can measure exactly what the placer reasoned
 * about, rather than re-deriving it. */
export function labelBoxAt(l: LabelBox, y = l.y): Box {
  return {
    key: l.key,
    left: l.x - l.width / 2,
    right: l.x + l.width / 2,
    top: y - l.height * 0.8,
    bottom: y + l.height * 0.2,
  }
}

export function boxesOverlap(a: Box, b: Box): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom
}

/**
 * EXACT box-vs-ellipse test. Scaling by (1/rx, 1/ry) turns the ellipse into a
 * unit circle and leaves the axis-aligned box axis-aligned, so the question
 * becomes "is the closest point of the scaled box within 1 of the origin".
 *
 * Round 3 needed this: the old version sampled nine points on the box and
 * missed an ellipse edge that crossed between them.
 */
export function boxHitsEllipse(box: Box, e: Ellipse, margin = 0): boolean {
  const rx = e.rx + margin
  const ry = e.ry + margin
  if (rx <= 0 || ry <= 0) return false
  // Closest point of the scaled box to the origin, per axis.
  const l = (box.left - e.cx) / rx
  const r = (box.right - e.cx) / rx
  const t = (box.top - e.cy) / ry
  const b = (box.bottom - e.cy) / ry
  const cx = l > 0 ? l : r < 0 ? r : 0
  const cy = t > 0 ? t : b < 0 ? b : 0
  return cx * cx + cy * cy <= 1
}

export function boxHitsDisc(box: Box, d: Disc, margin = 0): boolean {
  const nx = Math.min(Math.max(d.cx, box.left), box.right)
  const ny = Math.min(Math.max(d.cy, box.top), box.bottom)
  return Math.hypot(d.cx - nx, d.cy - ny) <= d.r + margin
}

/**
 * EXACT box-vs-segment test (Liang–Barsky clipping of the segment against the
 * box, grown by `margin`).
 *
 * Round 3 needed this too: the old version asked only whether the box's four
 * corners and centre were near the line, so a near-vertical arrow passing
 * through the MIDDLE of a wide name went undetected — which is exactly how the
 * F4 and F5 dashes ended up running through "Solid Oxide Fuel Cell Stack
 * (Duplicate Filing)".
 */
export function boxHitsSegment(box: Box, s: Segment, margin = 0): boolean {
  const left = box.left - margin
  const right = box.right + margin
  const top = box.top - margin
  const bottom = box.bottom + margin
  let t0 = 0
  let t1 = 1
  const dx = s.b[0] - s.a[0]
  const dy = s.b[1] - s.a[1]
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0 // parallel: inside only if not outside the slab
    const r = q / p
    if (p < 0) {
      if (r > t1) return false
      if (r > t0) t0 = r
    } else {
      if (r < t0) return false
      if (r < t1) t1 = r
    }
    return true
  }
  return (
    clip(-dx, s.a[0] - left) &&
    clip(dx, right - s.a[0]) &&
    clip(-dy, s.a[1] - top) &&
    clip(dy, bottom - s.a[1])
  )
}

export interface LabelObstacles {
  footprints?: Ellipse[]
  discs?: Disc[]
  segments?: Segment[]
  /** Pills and frontier labels — already drawn, and anchored to real geometry,
   * so a NAME moves out of their way rather than the other way round. */
  boxes?: Box[]
  /** The mark each label belongs to, by key. A slot further than `cap` from its
   * own mark is penalised, so a name stays with the thing it names unless the
   * only clean slot is further away — in which case the caller draws a leader.
   * (Review round 4, item 3: a name ended up 183px from its own star with the
   * NEAREST name to that star belonging to a different invention.) */
  anchors?: Map<string, Point>
  cap?: number
  /** Clear space every label keeps from the canvas edge. The figure never sets
   * type against the frame; F2's label came out 3px from the top (review round
   * 6, item 2). */
  edgePad?: number
  /** Clear space demanded around every `segments` obstacle — the arrow shafts
   * and heads. 8px keeps an arrowhead from reading as a mark appended to a
   * name, which is what F4's head 5px from a ")" was doing. */
  segmentPad?: number
  /** For a label that is joined to something by a drawn line — a frontier
   * label to its own arrowhead — the point that line must reach. A candidate
   * whose connector would cross a `boxes` obstacle is penalised, so the label
   * stays on the same side of a name as the arrow it belongs to. */
  connectors?: Map<string, Point>
  /** Clear space a LEADER keeps from every document dot. Set it and the leader
   * check treats the 4px specks as real marks — a pale dashed line that passes
   * through one reads as pointing at that dot. Left unset the dots are ignored,
   * which is right for a frontier label's short run to its own arrowhead.
   * (Review round 7, item 1: the leader's centre-line passed 0.6px from a dot's
   * centre — the dot sat ON the line.) */
  leaderClearDots?: number
  /** Ink a LEADER must keep LEADER_INK_CLEARANCE away from but which is not an
   * obstacle to anything else: the frontier labels and the arrowheads. Round 8
   * ran a leader's horizontal straight through the F6 label — no crossing, no
   * overlap with any box then being tested, and completely unreadable. */
  leaderInk?: Box[]
  /**
   * THE ONE LINE A LEADER MAY CROSS. Everything in `segments` is a hard no —
   * a leader across a frontier ray reads as an arrow that changed direction.
   * The dashed BRIDGE is different: both are pale dashed lines, a crossing
   * between them reads as two lines meeting rather than one line breaking, and
   * on this map every straight leader crosses it, so banning it banned the
   * figure's own shape. Allowed exactly once, at a readable angle, and well
   * clear of the bridge's ends and of the amber ground it names.
   * (Coordinator ruling, review round 10.)
   */
  bridge?: { runs: Segment[]; ends: Point[]; keepOut?: Box[] }
  /** Lines a LABEL BOX must keep off, when that is not the same list a LEADER
   * must not cross. The bridge is exactly that case: a name drawn across the
   * dashed corridor is as unreadable as one across a ray, but a leader may
   * cross it once. Defaults to `segments`. */
  boxSegments?: Segment[]
  /** The area a leader may be drawn in. The canvas inset by a few pixels: a
   * leader routed along the very edge of the frame reads as part of the border
   * rather than as a line pointing at something. */
  leaderBounds?: Box
  /** Clear space demanded around every `boxes` obstacle. A frontier label 3px
   * from an invention's name, on the same baseline and at the same weight,
   * reads as the last word of that name — so a gap is not clearance until it is
   * bigger than a word space (review round 5, item 1). */
  boxPad?: number
  /** When a label lands outside `cap`, the caller draws a leader to its mark.
   * Set this and the solver checks that leader would be CLEAR before choosing
   * the slot, so it never trades "beside its mark" for "joined by a line drawn
   * through somebody else's invention" (review round 5, item 3). */
  checkLeader?: boolean
  /**
   * IS A NAME ALLOWED TO SIT ON ITS OWN CENTRE STAR? Default false — a label is
   * never an obstacle to the mark it names, which is what lets an atlas name sit
   * hard against its own shape.
   *
   * S5 ROUND 2 SHOWED WHY A MAP MAY WANT THE OPPOSITE. The entity map paints its
   * names with a 2.6px #F8FBFD halo so they read over the grey density; a name
   * placed across its own star therefore ERASES that star. On the live plot
   * "Vibration-Isolated Sensor Mount · 2 documents" left 53 of 142 strong-amber
   * pixels in the mark's 54x50 box, and it was the only name on the map whose
   * star could not be seen at all — the exact "name overlapping ink" defect
   * addendum 8 lists beside a leader. Set this and NO slot may sit on ANY star,
   * its own included.
   *
   * The pairing term (NEARER_ANOTHER_STAR) is unaffected either way: it always
   * compares a box's distance to its OWN star against every other, which is a
   * different question from whether the box may cover one.
   */
  ownDiscIsObstacle?: boolean
  /**
   * DOCUMENT DOTS A NAME MAY NOT COVER — a list of its own, and empty by
   * default, so the atlas's rule is untouched.
   *
   * `discs` deliberately IGNORES a small keyless speck: the atlas is covered in
   * document dots, treating them as marks drove a name 123px from its own star,
   * and the figure itself puts names over their own clusters. But a keyed disc
   * in that list is also a STAR to the pairing term, so dots cannot simply be
   * added there — every other project's dot would then count as "another
   * invention's mark" for the nearest-mark rule, which is a different and much
   * stricter question than the one that rule asks.
   *
   * S5 ROUND 3. On a plot whose names carry a halo, a dot IS a datum and the
   * halo erases it: the S5x capture lost a whole purple dot of the CleanTech
   * mark under the word "documents" (31 strong-purple pixels in the union, zero
   * after), so the plot drew 29 dots while the label said 30. These are
   * therefore priced — cumulatively, one HIT_DOT each, so a slot that covers
   * fewer wins — but priced BELOW NEARER_ANOTHER_STAR, because a name paired
   * with the wrong mark is a worse failure than a covered speck, and that is a
   * trade the solver has to be able to make.
   */
  dots?: Disc[]
  /**
   * WHAT IT COSTS TO SIT ON YOUR OWN FOOTPRINT. Default 0 — free, which is the
   * atlas's rule: the figure hangs a name against the shape it names.
   *
   * A map that paints a halo wants a preference rather than a licence. The S5x
   * capture showed two arc nicks (57px and 48px of outline erased) where a name
   * crossed its own footprint's edge, and the S5 figure draws all three of its
   * names clear of their footprints. Set a small number and a slot clear of its
   * own shape wins over one that nicks it, while a crowded map can still fall
   * back to nicking rather than flinging the name away — it is priced BELOW
   * OUT_OF_CAP, so staying with its own mark still matters more.
   */
  ownEllipseCost?: number
}

/** How far a label's box is from a point — 0 when the point is inside it. Used
 * for the cap above and by the tests that assert it. */
export function boxDistanceTo(box: Box, p: Point): number {
  const nx = Math.min(Math.max(p[0], box.left), box.right)
  const ny = Math.min(Math.max(p[1], box.top), box.bottom)
  return Math.hypot(p[0] - nx, p[1] - ny)
}

/** The same box with `pad` of clear space added on every side. A gap of a few
 * pixels between two pieces of text of the same weight is not clearance — it
 * reads as a word space (review round 5, item 1: "Cold-Chain Vaccine F5"). */
export function inflate(box: Box, pad: number): Box {
  return {
    key: box.key,
    left: box.left - pad,
    top: box.top - pad,
    right: box.right + pad,
    bottom: box.bottom + pad,
  }
}

/**
 * THE LEADER A LABEL WOULD NEED, from the point of its own box nearest the
 * mark to the mark itself. Drawn from the nearest side, so the line touches the
 * text rather than starting inside it — and so the side it leaves by is chosen
 * by the geometry rather than fixed to top or bottom.
 */
export function leaderFrom(box: Box, anchor: Point): Segment {
  return {
    a: [
      Math.min(Math.max(anchor[0], box.left), box.right),
      Math.min(Math.max(anchor[1], box.top), box.bottom),
    ],
    b: anchor,
  }
}

/**
 * Would that leader cross something? A leader has to obey exactly the rule the
 * frontier rays now pass — never across a footprint, a star or a label (review
 * round 5, item 3: the Solid Oxide leader cut an 83px chord through the Marine
 * footprint, passed 12px from its star and ran behind its name).
 *
 * A label's OWN footprint and OWN star are skipped, because the star it points
 * at sits at the centre of its own shape: a leader that reaches its own mark
 * must cross its own shape, and that is the one crossing that carries meaning.
 */

/** Where two segments meet, or nothing when they do not. */
export function segmentIntersection(p: Segment, q: Segment): Point | null {
  const r: Point = [p.b[0] - p.a[0], p.b[1] - p.a[1]]
  const t: Point = [q.b[0] - q.a[0], q.b[1] - q.a[1]]
  const denom = r[0] * t[1] - r[1] * t[0]
  if (Math.abs(denom) < 1e-12) return null
  const qp: Point = [q.a[0] - p.a[0], q.a[1] - p.a[1]]
  const u = (qp[0] * t[1] - qp[1] * t[0]) / denom
  const v = (qp[0] * r[1] - qp[1] * r[0]) / denom
  if (u < 0 || u > 1 || v < 0 || v > 1) return null
  return [p.a[0] + r[0] * u, p.a[1] + r[1] * u]
}

/** The sharper of the two angles between two lines, in degrees (0-90). */
export function crossingAngle(p: Segment, q: Segment): number {
  const a = angleOf(p.a, p.b)
  const b = angleOf(q.a, q.b)
  const d = angleGap(a, b)
  return d > 90 ? 180 - d : d
}

/** How a route meets the bridge: not at all, or once at a point. */
export function bridgeMeeting(
  runs: Segment[],
  bridge: { runs: Segment[]; ends: Point[]; keepOut?: Box[] },
): { count: number; at?: Point; angle?: number; clearOfEnds?: number } {
  const hits: { at: Point; angle: number }[] = []
  for (const run of runs) {
    for (const b of bridge.runs) {
      const at = segmentIntersection(run, b)
      if (at) hits.push({ at, angle: crossingAngle(run, b) })
    }
  }
  if (hits.length === 0) return { count: 0 }
  const first = hits[0]
  const clearOfEnds = Math.min(
    ...bridge.ends.map((e) => Math.hypot(first.at[0] - e[0], first.at[1] - e[1])),
    ...(bridge.keepOut ?? []).map((k) => boxDistanceTo(k, first.at)),
  )
  return { count: hits.length, at: first.at, angle: first.angle, clearOfEnds }
}

/** Distance from a point to a segment. */
export function pointToSegment(p: Point, s: Segment): number {
  const dx = s.b[0] - s.a[0]
  const dy = s.b[1] - s.a[1]
  const len2 = dx * dx + dy * dy
  const t = len2 < 1e-12 ? 0 : Math.min(1, Math.max(0, ((p[0] - s.a[0]) * dx + (p[1] - s.a[1]) * dy) / len2))
  return Math.hypot(p[0] - (s.a[0] + dx * t), p[1] - (s.a[1] + dy * t))
}

/** Distance from a box to a segment — 0 when they touch. Exact for a convex
 * box: either they intersect, or the closest pair involves a box corner or a
 * segment endpoint. */
export function segmentToBox(s: Segment, box: Box): number {
  if (boxHitsSegment(box, s, 0)) return 0
  const corners: Point[] = [
    [box.left, box.top],
    [box.right, box.top],
    [box.right, box.bottom],
    [box.left, box.bottom],
  ]
  let best = Number.POSITIVE_INFINITY
  for (const c of corners) best = Math.min(best, pointToSegment(c, s))
  for (const p of [s.a, s.b]) {
    const nx = Math.min(Math.max(p[0], box.left), box.right)
    const ny = Math.min(Math.max(p[1], box.top), box.bottom)
    best = Math.min(best, Math.hypot(p[0] - nx, p[1] - ny))
  }
  return best
}

/**
 * THE ROUTE A LEADER TAKES, and how much clear space it leaves.
 *
 * A straight line is tried first, because that is what the figure draws. When
 * the straight line is blocked the leader is ROUTED instead — right-angled, the
 * way a technical drawing routes a leader round something — rather than the name
 * being flung somewhere else to make a straight line possible.
 *
 * ROUND 8 MADE CLEARANCE A NUMBER RATHER THAN A YES/NO. Round 7's fix picked the
 * FIRST route that crossed nothing, and the first route it found ran its long
 * horizontal 0.50px from the F6 label's ink — legal by every rule then in force
 * and unreadable on the page. So every candidate route is now scored by its
 * WORST clearance against every piece of ink it is not landing on, and the best
 * scoring route wins. The corner of the elbow slides, and the leader may leave
 * the box by any side, so the search can find the lane the reviewer's own
 * arithmetic showed was there.
 */
export function leaderRoute(
  box: Box,
  anchor: Point,
  key: string,
  obstacles: LabelObstacles,
  boxPad = 0,
  /**
   * `sweep` slides the elbow's corner over a range of lanes, which is what
   * finds a genuinely clear route but costs ~13ms. The NAME PLACER asks this
   * question once per candidate slot — nearly a thousand times per render — so
   * it uses the cheap form (straight plus the two plain elbows) and the full
   * sweep runs once, on the route that is actually drawn. Getting that the
   * wrong way round put an 11-second sweep inside the placement loop.
   */
  opts: { sweep?: boolean; target?: number; straightOnly?: boolean } = {},
): { runs: Segment[]; clearance: number; clearanceOwn: number; crossesBridge: boolean } | null {
  const nearest: Point = [
    Math.min(Math.max(anchor[0], box.left), box.right),
    Math.min(Math.max(anchor[1], box.top), box.bottom),
  ]
  // THE NEAREST POINT ON THE BOX TO THE MARK, and the nearest point on each of
  // the four edges — never a fixed fraction along an edge (review round 11,
  // item 6). A leader rising out of the MIDDLE of a two-line name ties itself
  // to nothing; one leaving the edge that faces the mark reads as belonging to
  // the text it starts at.
  const clampX = (v: number) => Math.min(Math.max(v, box.left), box.right)
  const clampY = (v: number) => Math.min(Math.max(v, box.top), box.bottom)
  const exits: Point[] = [nearest]
  if (opts.sweep) {
    exits.push([clampX(anchor[0]), box.top])
    exits.push([clampX(anchor[0]), box.bottom])
    exits.push([box.left, clampY(anchor[1])])
    exits.push([box.right, clampY(anchor[1])])
  }
  const lanes: number[] = []
  if (opts.sweep && !opts.straightOnly) for (let k = -140; k <= 140; k += 4) lanes.push(k)
  const target = opts.target ?? Number.POSITIVE_INFINITY
  const viewportArea = VIEWPORT.width * VIEWPORT.height

  type Route = { runs: Segment[]; clearance: number; clearanceOwn: number; crossesBridge: boolean }
  // A STRAIGHT LINE IS THE PREFERRED SHAPE, not merely one candidate among
  // many. The figure draws no leaders at all and puts every name directly under
  // its own star, so one straight line is the least foreign thing that can be
  // added; an elbow has to EARN its place by a real margin of clearance rather
  // than by a hair. Tracked separately for exactly that reason.
  let bestStraight: Route | null = null
  let bestRouted: Route | null = null
  const consider = (runs: Segment[]) => {
    const usable = runs.filter((seg) => Math.hypot(seg.b[0] - seg.a[0], seg.b[1] - seg.a[1]) >= 0.5)
    if (usable.length === 0) return
    let worst = Number.POSITIVE_INFINITY
    let ownWorst = Number.POSITIVE_INFINITY
    for (let i = 0; i < usable.length; i += 1) {
      const c = leaderRunClearance(
        usable[i], anchor, key, obstacles, boxPad, i === usable.length - 1, i === 0,
      )
      if (c.other < 0) return
      worst = Math.min(worst, c.other)
      ownWorst = Math.min(ownWorst, c.own)
    }
    // A LEADER MAY NOT SPRAWL (review round 11, item 4). Both caps are on the
    // whole path: the rectangle it bounds, and how far it travels against the
    // straight-line distance it is standing in for.
    const straightDist = Math.hypot(anchor[0] - usable[0].a[0], anchor[1] - usable[0].a[1])
    const pathLen = usable.reduce((n, r) => n + Math.hypot(r.b[0] - r.a[0], r.b[1] - r.a[1]), 0)
    if (straightDist > 1 && pathLen > straightDist * LEADER_MAX_LENGTH_FACTOR) return
    const xs = usable.flatMap((r) => [r.a[0], r.b[0]])
    const ys = usable.flatMap((r) => [r.a[1], r.b[1]])
    const area = (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys))
    if (area > viewportArea * LEADER_MAX_AREA_FRACTION) return

    // THE ONE ALLOWED CROSSING, checked on the WHOLE route rather than run by
    // run, because "at most once" is a property of the path.
    let crossesBridge = false
    if (obstacles.bridge) {
      const met = bridgeMeeting(usable, obstacles.bridge)
      if (met.count > 0) {
        if (met.count > 1) return
        if ((met.angle ?? 0) < BRIDGE_CROSS_MIN_ANGLE) return
        if ((met.clearOfEnds ?? 0) < BRIDGE_CROSS_MIN_CLEAR) return
        crossesBridge = true
      }
    }
    // A leader may not box anything in — three sides of a rectangle round
    // another invention's mark is a selection box, not a leader.
    if (usable.length > 1) {
      // NOTHING THE MAP DRAWS MAY BE BOXED IN — not just names (review round
      // 11, item 3). Round 10's rule tested only keyed name boxes, so a route
      // could and did bound a label, an arrowhead, a ray and a centre star on
      // three sides each and still pass.
      const boxed: Box[] = [
        ...(obstacles.boxes ?? []).filter((b) => b.key === undefined || b.key !== key),
        ...(obstacles.leaderInk ?? []).filter((b) => b.key === undefined || b.key !== key),
        ...(obstacles.discs ?? []).filter((d) => d.key !== undefined && d.key !== key).map(boxOfDisc),
        ...(obstacles.footprints ?? []).filter((e) => e.key !== undefined && e.key !== key).map(boxOfEllipse),
        ...(obstacles.segments ?? []).map((seg) => boxOfSegment(seg)),
      ]
      if (boxed.some((b) => enclosesBox(usable, b))) return
    }
    // A shorter path wins a tie: the straight line is listed first and the
    // sliding lanes after, so equal clearance keeps the simpler drawing.
    const straight = usable.length === 1
    const current = straight ? bestStraight : bestRouted
    if (current === null || worst > current.clearance + 0.001) {
      const found = {
        runs: usable,
        clearance: worst,
        clearanceOwn: ownWorst === Number.POSITIVE_INFINITY ? 999 : ownWorst,
        crossesBridge,
      }
      if (straight) bestStraight = found
      else bestRouted = found
    }
  }

  for (const start of exits) {
    consider([{ a: start, b: anchor }])
    if (opts.straightOnly) continue
    consider([{ a: start, b: [anchor[0], start[1]] }, { a: [anchor[0], start[1]], b: anchor }])
    consider([{ a: start, b: [start[0], anchor[1]] }, { a: [start[0], anchor[1]], b: anchor }])
    if (bestStraight !== null && (bestStraight as Route).clearance >= target) return bestStraight
    for (const k of lanes) {
      if (bestStraight !== null && (bestStraight as Route).clearance >= target) return bestStraight
      // Out to a lane, along it, then in onto the mark — both orientations.
      const y = start[1] + k
      if (y > 2 && y < 2000) {
        consider([
          { a: start, b: [start[0], y] },
          { a: [start[0], y], b: [anchor[0], y] },
          { a: [anchor[0], y], b: anchor },
        ])
      }
      const x = start[0] + k
      if (x > 2 && x < 2000) {
        consider([
          { a: start, b: [x, start[1]] },
          { a: [x, start[1]], b: [x, anchor[1]] },
          { a: [x, anchor[1]], b: anchor },
        ])
      }
    }
  }
  // A STRAIGHT LEADER WINS WHENEVER ONE EXISTS. Coordinator ruling, round 9:
  // the figure draws no leaders at all and puts every name directly under its
  // own star, so one straight line is the least foreign thing that can be added
  // — and a routed one, however much clearance it buys, introduces a shape the
  // legend does not key. The elbow is the fallback for a name with no straight
  // line available, not a competitor on clearance.
  return bestStraight ?? bestRouted
}

/** The route only. */
export function leaderPath(
  box: Box,
  anchor: Point,
  key: string,
  obstacles: LabelObstacles,
  boxPad = 0,
): Segment[] | null {
  const straight = leaderFrom(box, anchor)
  if (Math.hypot(straight.b[0] - straight.a[0], straight.b[1] - straight.a[1]) < 1) return []
  return leaderRoute(box, anchor, key, obstacles, boxPad)?.runs ?? null
}

/** Would that leader cross something? True when no route is clear. */
export function leaderBlocked(
  box: Box,
  anchor: Point,
  key: string,
  obstacles: LabelObstacles,
  boxPad = 0,
): boolean {
  return leaderPath(box, anchor, key, obstacles, boxPad) === null
}

/**
 * How much clear space one run of a leader leaves, or -1 when it is blocked
 * outright. A label's OWN footprint and OWN star are skipped, because the star
 * it points at sits at the centre of its own shape: a leader that reaches its
 * own mark must cross its own shape, and that is the one crossing that carries
 * meaning.
 */
function leaderRunClearance(
  seg: Segment,
  anchor: Point,
  key: string,
  obstacles: LabelObstacles,
  boxPad: number,
  arriving: boolean,
  first = false,
): { other: number; own: number } {
  // THE HARD PAD MUST BE SMALLER THAN THE TARGET, or the target is unreachable
  // by construction. It was max(boxPad, 4) — 8 for the leader passes — which
  // REFUSED every route closer than 8px to a name while the review's own target
  // was a 7.6px clearance. Nothing could satisfy both, so the router returned
  // null even at the slot the reviewer had measured a 6.0px straight from. The
  // hard rule is "not drawn on it"; the room it leaves is the number.
  const SHAPE_CLEAR = 2
  const TEXT_CLEAR = 2
  void boxPad
  let worst = Number.POSITIVE_INFINITY
  let ownGap = Number.POSITIVE_INFINITY
  const bounds = obstacles.leaderBounds
  if (bounds) {
    for (const p of [seg.a, seg.b]) {
      if (p[0] < bounds.left || p[0] > bounds.right || p[1] < bounds.top || p[1] > bounds.bottom) {
        return { other: -1, own: -1 }
      }
    }
  }
  const blocked = { other: -1, own: -1 }
  for (const e of obstacles.footprints ?? []) {
    if (e.key !== undefined && e.key === key) continue
    if (segmentHitsEllipse(seg, e, SHAPE_CLEAR)) return blocked
  }
  for (const d of obstacles.discs ?? []) {
    if (d.key !== undefined && d.key === key) continue
    if (d.key === undefined && d.r <= 5) {
      // A DOCUMENT DOT IS A MARK TO A LINE, EVEN THOUGH IT IS NOT ONE TO A
      // BLOCK OF TEXT (round 6 established the second half; round 7 the first).
      if (obstacles.leaderClearDots === undefined) continue
      // Except on the run that is ARRIVING: it lands on its own star, which
      // sits at the centre of its own cluster, so the dots it must pass to get
      // there are the ones that belong to it.
      if (Math.hypot(d.cx - anchor[0], d.cy - anchor[1]) <= APPROACH) continue
      const run = arriving ? trimToAnchor(seg, APPROACH) : seg
      const gap = pointToSegment([d.cx, d.cy], run)
      if (gap < obstacles.leaderClearDots) return blocked
      worst = Math.min(worst, gap)
      continue
    }
    if (segmentHitsDisc(seg, d, SHAPE_CLEAR)) return blocked
    worst = Math.min(worst, pointToSegment([d.cx, d.cy], seg) - d.r)
  }
  for (const b of obstacles.boxes ?? []) {
    if (b.key !== undefined && b.key === key) {
      // ITS OWN NAME IS STILL INK (review round 9, item 1b). Skipping it
      // entirely is what let a leader graze 2.00px past its own ")" — the
      // leader has to LEAVE that box, so only the first few pixels of the first
      // run are exempt, and everything after them is measured like any other.
      // ITS OWN NAME IS MEASURED SEPARATELY AND NEVER FOLDED INTO THE HEADLINE
      // NUMBER (review round 11, item 5). The leader starts on this box, so the
      // trimmed distance is the trim constant by construction — publishing that
      // as "clearance" said 5.00 when the line really ran 2.24px from a glyph.
      // It is refused if it runs back through its own name, and reported as its
      // own figure.
      const away = first ? trimFromStart(seg, OWN_NAME_EXIT) : seg
      if (Math.hypot(away.b[0] - away.a[0], away.b[1] - away.a[1]) >= 0.5) {
        if (boxHitsSegment(inflate(b, OWN_NAME_PAD), away, 0)) return blocked
        ownGap = Math.min(ownGap, segmentToBox(first ? seg : away, b))
      }
      continue
    }
    if (boxHitsSegment(inflate(b, TEXT_CLEAR), seg, 0)) return blocked
    worst = Math.min(worst, segmentToBox(seg, b))
  }
  // ARROWHEADS AND LABELS. Round 8 drew a leader through the F6 label and 4.5px
  // from an arrowhead: neither is a crossing, so neither of round 7's rules saw
  // it. They are scored here, and a run closer than LEADER_INK_CLEARANCE to
  // either is refused outright.
  for (const ink of obstacles.leaderInk ?? []) {
    if (ink.key !== undefined && ink.key === key) continue
    const gap = segmentToBox(seg, ink)
    // TOUCHING IS A HARD NO; SHORT OF THE TARGET IS A SCORE. Refusing every
    // route under LEADER_INK_CLEARANCE was tried and it is wrong: on this map
    // no route reaches 10px, so the refusal deleted the leader altogether and
    // left the name with nothing joining it to its mark — worse than a tight
    // one. The floor is what the search MAXIMISES toward, and the number it
    // actually achieved is published on the drawn element so the review can
    // read it off the page instead of measuring pixels.
    if (gap <= 0) return blocked
    worst = Math.min(worst, gap)
  }
  // NO LINE MAY CROSS ANOTHER LINE. The figure has not one crossing on the map
  // (review round 7, item 2). Only the name pass supplies `segments`, so a
  // frontier label's own run to its own head is never measured against the very
  // arrow it belongs to.
  for (const other of obstacles.segments ?? []) {
    if (segmentsCross(seg, other)) return blocked
    worst = Math.min(worst, segmentToSegment(seg, other))
  }
  return { other: worst === Number.POSITIVE_INFINITY ? 999 : worst, own: ownGap }
}

/** Diagnostic: what is nearest this run, and how near. Not used by the drawing
 * — it exists so a review question about a published clearance number can be
 * answered with the obstacle's name rather than a guess. */
export function leaderBinder(
  runs: Segment[],
  key: string,
  obstacles: LabelObstacles,
): { what: string; gap: number } {
  let what = 'nothing'
  let gap = Number.POSITIVE_INFINITY
  const note = (label: string, d: number) => {
    if (d < gap) { gap = d; what = label }
  }
  for (const seg of runs) {
    for (const d of obstacles.discs ?? []) {
      if (d.key !== undefined && d.key === key) continue
      if (d.key === undefined && d.r <= 5) continue
      note(`star ${d.key ?? '?'}`, pointToSegment([d.cx, d.cy], seg) - d.r)
    }
    for (const b of obstacles.boxes ?? []) {
      if (b.key !== undefined && b.key === key) continue
      note(`box ${b.key ?? '?'}`, segmentToBox(seg, b))
    }
    for (const ink of obstacles.leaderInk ?? []) {
      if (ink.key !== undefined && ink.key === key) continue
      note(`ink ${ink.key ?? '?'}`, segmentToBox(seg, ink))
    }
    for (const other of obstacles.segments ?? []) {
      note('line', segmentToSegment(seg, other))
    }
  }
  return { what, gap }
}

/**
 * DOES THIS POLYLINE BOX SOMETHING IN? Three sides of a rectangle around
 * another invention's name reads as a selection box, not as a leader — the
 * round-9 leader drew exactly that around "Cold-Chain Vaccine Logistics
 * Tracker", the F5 label, its arrowhead and its whole ray, and the reviewer
 * logged it as a selection box on first sight. It is the only dotted rectangle
 * on the map and the legend keys nothing like it.
 *
 * Measured by casting from the thing's centre in the four axis directions and
 * counting how many of them meet the polyline: three or more and it is boxed in.
 */
export function enclosesBox(runs: Segment[], box: Box, reach = 4000): boolean {
  // PROBE THE SPAN, NOT THE CENTRE (review round 11). Round 10's version cast
  // four rays from the middle of the object and passed by about 3px: a route
  // can bound something on three full sides while missing the single centre
  // ray on one of them. The four edge midpoints are probed as well, and a side
  // counts as closed if ANY probe on it is crossed.
  const cx = (box.left + box.right) / 2
  const cy = (box.top + box.bottom) / 2
  const from: Point[] = [
    [cx, cy],
    [cx, box.top],
    [cx, box.bottom],
    [box.left, cy],
    [box.right, cy],
  ]
  const sides: Point[] = [
    [0, -reach],
    [0, reach],
    [-reach, 0],
    [reach, 0],
  ]
  let closed = 0
  for (const [dx, dy] of sides) {
    const anyCrossed = from.some((o) =>
      runs.some((r) => segmentsCross({ a: o, b: [o[0] + dx, o[1] + dy] }, r)),
    )
    if (anyCrossed) closed += 1
  }
  return closed >= 3
}

/** An object a leader may not box in — reduced to the rectangle it occupies. */
export function boxOfEllipse(e: Ellipse): Box {
  return { key: e.key, left: e.cx - e.rx, top: e.cy - e.ry, right: e.cx + e.rx, bottom: e.cy + e.ry }
}
export function boxOfDisc(d: Disc): Box {
  return { key: d.key, left: d.cx - d.r, top: d.cy - d.r, right: d.cx + d.r, bottom: d.cy + d.r }
}
export function boxOfSegment(s: Segment, key?: string): Box {
  return {
    key,
    left: Math.min(s.a[0], s.b[0]),
    top: Math.min(s.a[1], s.b[1]),
    right: Math.max(s.a[0], s.b[0]),
    bottom: Math.max(s.a[1], s.b[1]),
  }
}

/** Distance between two segments that do not cross. */
function segmentToSegment(p: Segment, q: Segment): number {
  return Math.min(
    pointToSegment(p.a, q),
    pointToSegment(p.b, q),
    pointToSegment(q.a, p),
    pointToSegment(q.b, p),
  )
}

/** How much of a leader's far end is its approach to its own mark. */
const APPROACH = 14

/** How much of a leader's near end is it leaving its own name box. */
const OWN_NAME_EXIT = 5

/** Clear space a leader keeps from its OWN name once it has left it. */
const OWN_NAME_PAD = 3

/** The leader minus its first `by` pixels — the part that has left the box. */
function trimFromStart(seg: Segment, by: number): Segment {
  const dx = seg.b[0] - seg.a[0]
  const dy = seg.b[1] - seg.a[1]
  const len = Math.hypot(dx, dy)
  if (len <= by) return { a: seg.b, b: seg.b }
  const t = by / len
  return { a: [seg.a[0] + dx * t, seg.a[1] + dy * t], b: seg.b }
}

/** The leader minus its last `by` pixels — the part that is travelling rather
 * than arriving. */
function trimToAnchor(seg: Segment, by: number): Segment {
  const dx = seg.b[0] - seg.a[0]
  const dy = seg.b[1] - seg.a[1]
  const len = Math.hypot(dx, dy)
  if (len <= by) return { a: seg.a, b: seg.a }
  const t = (len - by) / len
  return { a: seg.a, b: [seg.a[0] + dx * t, seg.a[1] + dy * t] }
}

/**
 * Do these two drawn lines actually cross? Proper segment intersection — the
 * sign of the cross products on both sides — not a bounding-box guess, which
 * would call two lines that merely share a rectangle a crossing.
 */
export function segmentsCross(p: Segment, q: Segment): boolean {
  const cross = (o: Point, a: Point, b: Point): number =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const d1 = cross(q.a, q.b, p.a)
  const d2 = cross(q.a, q.b, p.b)
  const d3 = cross(p.a, p.b, q.a)
  const d4 = cross(p.a, p.b, q.b)
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true
  }
  // Touching counts: a line that ends exactly on another still reads as one
  // line running into the other.
  const on = (a: Point, b: Point, p0: Point, d: number): boolean =>
    Math.abs(d) < 1e-9 &&
    Math.min(a[0], b[0]) - 1e-9 <= p0[0] && p0[0] <= Math.max(a[0], b[0]) + 1e-9 &&
    Math.min(a[1], b[1]) - 1e-9 <= p0[1] && p0[1] <= Math.max(a[1], b[1]) + 1e-9
  return (
    on(q.a, q.b, p.a, d1) || on(q.a, q.b, p.b, d2) || on(p.a, p.b, q.a, d3) || on(p.a, p.b, q.b, d4)
  )
}

/**
 * Place each label clear of everything the map has already drawn.
 *
 * ROUND 3 REWROTE THIS. Round 2 moved names off each other and off footprints,
 * but it did not know about the B/H pills or the frontier labels at all, and its
 * segment test only sampled corners — so a name still landed on the B1 pill and
 * on another invention's centre star, the F6 label ended up under a name, and
 * two arrow lines ran straight through one. The obstacle set now includes every
 * drawn box, and the ellipse and segment tests are exact rather than sampled.
 *
 * Candidates move DOWN and UP first (a name belongs under its own shape), then
 * sideways, nearest-first. Nothing is ever dropped: when no candidate is fully
 * clear the least-bad wins, because an unlabelled shape is worse than an
 * awkwardly placed name.
 */
/** 0, +step, -step, +2·step, -2·step … out to `max`. Below/right is listed
 * before above/left at each distance, and the candidate sort below is stable,
 * so a tie keeps that order — the figure hangs a name under its shape. */
function evenSteps(step: number, max: number): number[] {
  const out = [0]
  for (let v = step; v <= max; v += step) out.push(v, -v)
  return out
}

export function placeLabels(
  labels: LabelBox[],
  obstacles: LabelObstacles = {},
  viewport: { width: number; height: number } = VIEWPORT,
  /** How far a label may be moved. A frontier label has to stay beside its own
   * arrow head, so it gets a tighter grid than an invention name. */
  shift: { dx?: number[]; dy?: number[] } = {},
): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>()
  const placed: Box[] = []
  const footprints = obstacles.footprints ?? []
  const discs = obstacles.discs ?? []
  const segments = obstacles.segments ?? []
  const dots = obstacles.dots ?? []
  const boxes = obstacles.boxes ?? []

  const anchors = obstacles.anchors
  const cap = obstacles.cap
  const boxPad = obstacles.boxPad ?? 0
  const edgePad = obstacles.edgePad ?? 2
  const segmentPad = obstacles.segmentPad ?? 3
  const connectors = obstacles.connectors
  const ownDiscIsObstacle = obstacles.ownDiscIsObstacle ?? false
  const ownEllipseCost = obstacles.ownEllipseCost ?? 0
  // COLLISIONS ARE IN A DIFFERENT LEAGUE FROM PREFERENCES. Overlapping another
  // invention's mark is a fault the whole-screen assertions forbid; sitting
  // outside the cap, or needing a leader that crosses something, is a
  // preference between otherwise-clean slots. Scaling the collisions by 100
  // keeps their order exactly as it was and makes it impossible for any
  // preference to buy its way past one.
  const HIT_PLACED = 1000
  const HIT_BOX = 900
  const HIT_DISC = 800
  const HIT_SEGMENT = 700
  const HIT_ELLIPSE = 600
  const OUT_OF_CAP = 5
  const LEADER_BLOCKED = 20
  // An elbow costs about as much as being 6px short of the target: a straight
  // line at 5px still beats an elbow at 12px, which is the ranking the figure
  // asks for, while a straight that clears nothing at all still loses to a
  // roomy elbow.
  const ELBOW_SHAPE = 6
  const NEARER_ANOTHER_STAR = 300
  // A DOT IS A DATUM, NOT A MARK. Cumulative, so the solver minimises the ink it
  // erases rather than treating one covered dot the same as five; well above
  // every layout preference (OUT_OF_CAP 5, ownEllipseCost) so a clear slot
  // always wins; and below NEARER_ANOTHER_STAR (300) so a name never buys a
  // clean dot field by pairing itself with somebody else's star.
  const HIT_DOT = 90
  const NAME_GAP = 8
  const CONNECTOR_BLOCKED = 30
  // A REGULAR, FINER GRID. The old one stepped 46px across and ~10px down and
  // had no offsets between, so a slot that was clean AND had a drawable leader
  // could sit between two candidates and never be tried: on the live map the
  // nearest such slot for "Solid Oxide Fuel Cell Stack (Duplicate Filing)" was
  // 57px from its star, and the coarse grid's best was 97px. Below is listed
  // before above at every distance, because the figure hangs a name under its
  // shape and the sort below is stable, so ties keep this order.
  // The x reach spans the canvas, not a window around the anchor. The atlas's
  // own projection preserves shape, so a tall narrow set of inventions fills
  // less than half the width and leaves the rest empty — and round 6 measured
  // exactly that: the crowding that pushed a name onto its neighbours happened
  // with the right half of the map bare. A name may need that empty canvas and
  // a leader; it can only take it if the search can see it.
  const DY = shift.dy ?? evenSteps(12, 132)
  const DX = shift.dx ?? evenSteps(16, viewport.width)
  // Nearest-first over the whole grid, so a name stays with its own shape.
  const candidates: { dx: number; dy: number }[] = []
  for (const dy of DY) for (const dx of DX) candidates.push({ dx, dy })
  candidates.sort((a, b) => Math.hypot(a.dx, a.dy) - Math.hypot(b.dx, b.dy))

  for (const label of [...labels].sort((a, b) => a.y - b.y || a.x - b.x)) {
    // The fallback, when the grid offers nothing: the anchor pulled inside the
    // canvas by the label's OWN BOX, never by its baseline alone.
    let best = {
      x: Math.min(
        Math.max(label.x, edgePad + label.width / 2),
        viewport.width - edgePad - label.width / 2,
      ),
      y: Math.min(
        Math.max(label.y, edgePad + label.height * 0.8),
        viewport.height - edgePad - label.height * 0.2,
      ),
    }
    let bestCost = Number.POSITIVE_INFINITY
    const anchor = anchors?.get(label.key)
    for (const { dx, dy } of candidates) {
      const x = label.x + dx
      const y = label.y + dy
      const box = labelBoxAt({ ...label, x }, y)
      // ROUND 4, ITEM 1: clamp the whole BOX, not the baseline. A two-line name
      // is drawn 12px ABOVE its anchor, so a baseline-only guard of y >= 12 let
      // "Cold-Chain Vaccine Logistics Tracker" put its first line at y = 0 and
      // the canvas clipped it away — the map printed "Logistics Tracker", a name
      // no reader could match back to the roster.
      if (box.top < edgePad || box.bottom > viewport.height - edgePad) continue
      if (box.left < edgePad || box.right > viewport.width - edgePad) continue
      let cost = Math.hypot(dx, dy) / 1000
      // THE FIGURE'S OWN INVARIANT (review round 11, item 1): a name is nearer
      // its OWN star than any other. Every name in the figure sits 79-103 units
      // directly below its own mark and nearer to it than to anybody else's; a
      // slot that breaks that is illegal however clean it looks, because the
      // reader pairs a name with a mark by proximity before anything else.
      if (anchor !== undefined) {
        const ownD = boxDistanceTo(box, anchor)
        for (const d of discs) {
          if (d.key === undefined || d.key === label.key) continue
          if (boxDistanceTo(box, [d.cx, d.cy]) < ownD) {
            // Priced ABOVE the cap and a blocked leader, and BELOW every
            // collision. It is the strongest preference on this map, but it can
            // never buy itself a name drawn across an arrow or over another
            // invention's mark — that trade was tried and it is worse.
            cost += NEARER_ANOTHER_STAR
            break
          }
        }
        // …and directly below it is where the figure puts it.
        if (dy > 0 && Math.abs(dx) < 1) cost -= 0.5
      }
      const outOfCap = anchor !== undefined && cap !== undefined && boxDistanceTo(box, anchor) > cap
      if (outOfCap) cost += OUT_OF_CAP
      // A WORD SPACE IS NOT CLEARANCE — the rule frontier labels have had since
      // round 5, now applied between NAMES too. Bare non-overlap let two of
      // them settle 2px apart on one baseline, which reads as a single phrase
      // exactly the way "Cold-Chain Vaccine F5" did.
      for (const other of placed) {
        if (boxesOverlap(box, inflate(other, NAME_GAP))) cost += HIT_PLACED
      }
      for (const b of boxes) {
        // A label is never an obstacle to ITSELF. This matters once the caller
        // passes a first-pass position for every name so each one can be scored
        // against where all the others will actually be — without the skip,
        // every candidate would collide with its own provisional box.
        if (b.key !== undefined && b.key === label.key) continue
        if (boxesOverlap(box, inflate(b, boxPad))) cost += HIT_BOX
      }
      for (const e of footprints) {
        // A name may sit against its OWN shape — the figure puts it there. What
        // it may never do is run across somebody else's invention. A caller that
        // paints a halo can price its own shape as a PREFERENCE (ownEllipseCost)
        // so a slot clear of it wins, without making it a collision.
        if (e.key !== undefined && e.key === label.key) {
          if (ownEllipseCost > 0 && boxHitsEllipse(box, e, 2)) cost += ownEllipseCost
          continue
        }
        if (boxHitsEllipse(box, e, 2)) cost += HIT_ELLIPSE
      }
      for (const d of discs) {
        // ...and its own star, unless the caller has said no name may cover any
        // star at all (see ownDiscIsObstacle — a halo erases what it covers).
        if (!ownDiscIsObstacle && d.key !== undefined && d.key === label.key) continue
        // A DOCUMENT DOT IS NOT A MARK A LABEL CAN BE CONFUSED WITH. The dots
        // are 4px specks the map is covered in, and treating them as hard
        // obstacles is what drove "Solid Oxide Fuel Cell Stack (Duplicate
        // Filing)" 123px from its own star: every slot near the star sat on its
        // OWN cluster of dots, so the solver went looking for bare canvas and
        // came back with a leader drawn through another invention. The text
        // carries a 3.5px halo and the figure itself puts names over their own
        // clusters. A CENTRE STAR is a mark, and stays an obstacle.
        if (d.key === undefined && d.r <= 5) continue
        if (boxHitsDisc(box, d, 2)) cost += HIT_DISC
      }
      // The document dots, when the caller has said they are ink worth keeping.
      // Same 2px clearance every other obstacle is judged at.
      for (const d of dots) {
        if (boxHitsDisc(box, d, 2)) cost += HIT_DOT
      }
      for (const seg of obstacles.boxSegments ?? segments) {
        if (boxHitsSegment(box, seg, segmentPad)) cost += HIT_SEGMENT
      }
      // The line this label is joined to whatever it names by. A frontier label
      // separated from its own arrowhead by an invention's name reads as that
      // invention's caption and leaves the arrow unlabelled (review round 6,
      // item 2) — so a candidate on the wrong side of a name is priced.
      const connector = connectors?.get(label.key)
      if (connector && leaderBlocked(box, connector, label.key, obstacles, boxPad)) {
        cost += CONNECTOR_BLOCKED
      }

      // THE LEADER THIS SLOT COULD LEAVE — the joint solve (review round 10).
      // The name's position and its leader are chosen TOGETHER: a slot is worth
      // what the best leader it can actually leave is worth, with the frontier
      // labels, arrowheads and rays already known to the scorer. Deciding the
      // position first and routing afterwards is what produced a 12px leader
      // boxing in a neighbour, and then a 5px one that did not.
      //
      // PERFORMANCE GUARD, and it is load-bearing: this runs ONLY on a slot
      // that collides with nothing (`cost < HIT_ELLIPSE`), and only ever with
      // the CHEAP router — straight plus two plain elbows, no lane sweep. The
      // swept form costs ~13ms and there are ~900 candidates per label; putting
      // it in this loop made every atlas test take 8-11 seconds. The sweep runs
      // once, on the slot that wins.
      if (anchor !== undefined && outOfCap && obstacles.checkLeader && cost < HIT_ELLIPSE) {
        const withPlaced = { ...obstacles, boxes: [...boxes, ...placed] }
        const route = leaderRoute(box, anchor, label.key, withPlaced, boxPad, {})
        if (route === null) {
          cost += LEADER_BLOCKED
        } else {
          // A straight line is the figure's own shape, so an elbow is priced;
          // and among routes of a shape, more clear space is better, up to the
          // target beyond which extra room buys nothing.
          if (route.runs.length > 1) cost += ELBOW_SHAPE
          cost += Math.max(0, LEADER_TARGET_CLEARANCE - route.clearance)
        }
      }
      if (cost < bestCost) {
        bestCost = cost
        best = { x, y }
      }
      if (cost < 1) break
    }
    placed.push(labelBoxAt({ ...label, x: best.x }, best.y))
    out.set(label.key, best)
  }
  return out
}

/** Do these two labels' boxes overlap? Exported so a test can assert the
 * placement really separated them, rather than trusting the algorithm. */
export function labelsOverlap(a: LabelBox, b: LabelBox): boolean {
  return boxesOverlap(labelBoxAt(a), labelBoxAt(b))
}

/** Kept for the simpler callers: pull labels off each other only. */
export function spreadLabels(
  labels: { key: string; x: number; y: number }[],
  minGapY = 15,
  minGapX = 150,
): Map<string, number> {
  const out = new Map<string, number>()
  const placed: { x: number; y: number }[] = []
  for (const label of [...labels].sort((a, b) => a.y - b.y || a.x - b.x)) {
    let y = label.y
    let moved = true
    let guard = 0
    while (moved && guard < 40) {
      moved = false
      guard += 1
      for (const p of placed) {
        if (Math.abs(p.x - label.x) < minGapX && Math.abs(p.y - y) < minGapY) {
          y = p.y + minGapY
          moved = true
        }
      }
    }
    placed.push({ x: label.x, y })
    out.set(label.key, y)
  }
  return out
}

/** The figure's ★ invention centre, as a polygon points string. */
export function starPoints(cx: number, cy: number, outer: number, inner: number): string {
  const pts: string[] = []
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? outer : inner
    const a = (Math.PI / 5) * i - Math.PI / 2
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`)
  }
  return pts.join(' ')
}

/** The figure labels the shapes with the first word or two of the invention's
 * real name ("Aurora", "Solid-State") — the whole name will not fit under a
 * 240-wide map. Never a made-up short name: always a prefix of the real one. */
export function shortName(name: string): string {
  const first = name.split(/\s+/)[0] ?? name
  return first.length > 14 ? `${first.slice(0, 13)}…` : first
}

/**
 * Where the mini-map's amber score chip sits (review round 2, S4b item 1).
 *
 * It used to be pinned below the midpoint of the two centres, which put it
 * straight over the second invention's centre star. It now steps out along the
 * PERPENDICULAR of the pair's axis until it clears every star, so it still reads
 * as belonging to the shared ground between the two shapes without covering
 * either invention's own mark.
 */
export function scoreChipPosition(
  a: Point,
  b: Point,
  stars: Disc[],
  chip: { width: number; height: number },
  viewport: { width: number; height: number } = VIEWPORT,
): Point {
  const mid: Point = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
  let ax = b[0] - a[0]
  let ay = b[1] - a[1]
  const len = Math.hypot(ax, ay)
  if (len < 1) {
    ax = 1
    ay = 0
  } else {
    ax /= len
    ay /= len
  }
  // Perpendicular, pointing down the canvas so the chip reads as a caption.
  let px = -ay
  let py = ax
  if (py < 0) {
    px = -px
    py = -py
  }
  const halfW = chip.width / 2
  const halfH = chip.height / 2
  const hitsStar = (p: Point) =>
    stars.some((s) => {
      const nx = Math.min(Math.max(s.cx, p[0] - halfW), p[0] + halfW)
      const ny = Math.min(Math.max(s.cy, p[1] - halfH), p[1] + halfH)
      return Math.hypot(s.cx - nx, s.cy - ny) <= s.r + 3
    })
  const inBounds = (p: Point) =>
    p[0] - halfW >= 2 &&
    p[0] + halfW <= viewport.width - 2 &&
    p[1] - halfH >= 2 &&
    p[1] + halfH <= viewport.height - 2

  let fallback: Point = mid
  for (const distance of [18, 26, 34, 42, 50, 58]) {
    for (const sign of [1, -1]) {
      const p: Point = [mid[0] + px * distance * sign, mid[1] + py * distance * sign]
      if (!inBounds(p)) continue
      fallback = p
      if (!hitsStar(p)) return p
    }
  }
  return fallback
}

/**
 * Anchor each mini-map caption directly under its OWN shape, centred on that
 * shape (review round 3, item 8).
 *
 * Round 2 pushed the two apart along one baseline, which still left them two
 * pixels from each other — "Adaptive" and "Marine" read as one phrase. Two
 * captions now either clear each other by a comfortable gap on the same
 * baseline, or the second drops to the line below. Nothing is ever moved off
 * its own shape by more than the canvas requires.
 */
export interface CaptionPlacement {
  key: string
  x: number
  /** Which line it sits on: 0 is the lower row, 1 the row above it. */
  line: number
}

export function placeCaptions(
  captions: { key: string; x: number; width: number }[],
  viewport: { width: number; height: number } = VIEWPORT,
  minGap = 24,
): Map<string, CaptionPlacement> {
  const order = [...captions].sort((a, b) => a.x - b.x)
  const rows: { key: string; x: number; width: number }[][] = [[], []]
  const out = new Map<string, CaptionPlacement>()

  for (const c of order) {
    const x = Math.min(Math.max(c.x, c.width / 2 + 2), viewport.width - c.width / 2 - 2)
    // Prefer the shape's own line; drop to the second row only when the first
    // cannot hold it with a real gap.
    let line = 0
    for (; line < rows.length; line += 1) {
      const previous = rows[line][rows[line].length - 1]
      if (!previous) break
      if (x - previous.x >= (previous.width + c.width) / 2 + minGap) break
    }
    if (line >= rows.length) line = rows.length - 1
    rows[line].push({ key: c.key, x, width: c.width })
    out.set(c.key, { key: c.key, x, line })
  }
  return out
}

/** Kept for callers that only need the x. */
export function spreadCaptions(
  captions: { key: string; x: number; width: number }[],
  viewport: { width: number; height: number } = VIEWPORT,
  gap = 6,
): Map<string, number> {
  const order = [...captions].sort((a, b) => a.x - b.x)
  const placed: { key: string; x: number; width: number }[] = []
  for (const c of order) {
    let x = Math.min(Math.max(c.x, c.width / 2 + 2), viewport.width - c.width / 2 - 2)
    const previous = placed[placed.length - 1]
    if (previous) {
      const minX = previous.x + previous.width / 2 + gap + c.width / 2
      if (x < minX) x = minX
    }
    placed.push({ key: c.key, x, width: c.width })
  }
  const last = placed[placed.length - 1]
  if (last) {
    const overflow = last.x + last.width / 2 + 2 - viewport.width
    if (overflow > 0) {
      const shift = Math.min(overflow, placed[0].x - placed[0].width / 2 - 2)
      if (shift > 0) for (const p of placed) p.x -= shift
    }
  }
  return new Map(placed.map((p) => [p.key, p.x]))
}

/** Do these two captions' boxes overlap? Exported so a test can assert the
 * separation rather than trust the algorithm. */
export function captionsOverlap(
  a: { x: number; width: number },
  b: { x: number; width: number },
): boolean {
  return Math.abs(a.x - b.x) < (a.width + b.width) / 2
}

/** How wide a mini-map caption renders — the same estimate labelWidth uses, at
 * the mini-map's own 9.5px size, with a small floor so a short name still owns
 * enough room to be kept apart from its neighbour. */
export function captionWidthFor(text: string): number {
  return Math.max(30, text.length * 9.5 * 0.58)
}

/**
 * Break a long invention name over at most two lines.
 *
 * Round 3: "Solid Oxide Fuel Cell Stack (Duplicate Filing)" is 46 characters —
 * about 290px on a 900px canvas — and on a map this full there is simply no
 * 290px slot that clears every footprint, star, pill, arrow and other name. A
 * narrower, taller label has slots. The whole name is still shown: this wraps
 * it, it never truncates it, because a shortened name is a name the reader
 * cannot match to the roster.
 */
export function wrapLabel(
  text: string,
  maxWidth: number,
  fontSize = 11.5,
  measure: MeasureText = labelWidth,
): string[] {
  if (measure(text, fontSize) <= maxWidth) return [text]
  const words = text.split(/\s+/).filter(Boolean)
  if (words.length < 2) return [text]
  // Split at the word boundary that makes the two lines most even.
  let bestAt = 0
  let bestSpread = Number.POSITIVE_INFINITY
  for (let i = 1; i < words.length; i += 1) {
    // NEVER START A LINE WITH A BARE SEPARATOR. The entity map's labels read
    // "Cellmap Assay · 12 documents", and the most even split of that lands
    // exactly before the "·" — which prints a line beginning with a lone
    // middle dot, and reads as a bullet rather than as the rest of a name. The
    // separator stays on the line it followed.
    if (/^[·•\-\u2013\u2014]$/.test(words[i])) continue
    const a = measure(words.slice(0, i).join(' '), fontSize)
    const b = measure(words.slice(i).join(' '), fontSize)
    const spread = Math.abs(a - b) + Math.max(0, Math.max(a, b) - maxWidth) * 4
    if (spread < bestSpread) {
      bestSpread = spread
      bestAt = i
    }
  }
  // Every boundary was a separator, so there is nowhere to break that would not
  // start a line with one. One long line beats a line that reads as a bullet.
  if (bestAt === 0) return [text]
  return [words.slice(0, bestAt).join(' '), words.slice(bestAt).join(' ')]
}

/** The box a wrapped label occupies: as wide as its widest line, as tall as the
 * lines it takes. */
export function wrappedLabelSize(
  lines: string[],
  fontSize = 11.5,
  lineHeight = 12,
  measure: MeasureText = labelWidth,
): { width: number; height: number } {
  return {
    width: Math.max(...lines.map((l) => measure(l, fontSize))),
    height: lines.length * lineHeight,
  }
}
